import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button, Card, CardBody, Col, Input, Label, Nav, NavItem, NavLink,
  Row, Spinner, TabContent, TabPane, Table,
} from "reactstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import CompanyService from "../../services/company";
import SysAdminService from "../../services/sysadmin";
import AccountService from "../../services/account";

const DEFAULT_PARAMS = {
  rowStatus: "",
  loadingRoundingSum: 5000,
  minimumSumForReduction: 2500,
  minimumPerDevice: 3000,
  dayFraction: 0.35,
  specialDaysAmount: 1.2,
  weeklySum1: "",
  factor1: "",
  weeklySum2: "",
  factor2: "",
  weeklySum3: "",
  factor3: "",
  manuallyCounted: 30,
  cleaningCardReader: 45,
  smsCost: 0,
  whatsappCost: 0,
  whatsappUtilityCost: 0,
  whatsappMarketingCost: 0,
  whatsappAuthCost: 0,
  smsSegmentSize: 134,
};

function CompanySetup() {
  const { t } = useTranslation();
  const { companyId: paramCompanyId } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("1");
  const [isLoading, setIsLoading] = useState(false);
  const [companyId, setCompanyId] = useState(paramCompanyId ? parseInt(paramCompanyId) : null);
  const isEditMode = !!companyId;

  // Tab 1: Company Details
  const [companyName, setCompanyName] = useState("");

  // Tab 2: Company Admin
  const [existingAdmin, setExistingAdmin] = useState(null);
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPhone, setAdminPhone] = useState("");
  const [adminPassword, setAdminPassword] = useState("");

  // Tab 3: Company Parameters
  const [params, setParams] = useState({ ...DEFAULT_PARAMS });

  // Tab 5: Messages to Company
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (isEditMode) {
      loadCompanyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadCompanyData = async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const [companiesRes, paramsRes, messagesRes, usersRes] = await Promise.all([
        SysAdminService.getCompaniesStats(),
        SysAdminService.getCompanyParameters(companyId),
        SysAdminService.getMessagesToCompany(companyId),
        AccountService.listUsers({ companyId }),
      ]);

      // Company details
      if (companiesRes.data?.status === 200 && companiesRes.data.data) {
        const company = companiesRes.data.data.find((c) => c.id === companyId);
        if (company) {
          setCompanyName(company.companyName || "");
          setAdminPhone(company.phone || "");
        }
      }

      // Admin user (roleId=2)
      if (usersRes.data?.status === 200 && usersRes.data.dataList) {
        const admin = usersRes.data.dataList.find((u) => u.roleId === 2);
        if (admin) {
          setExistingAdmin(admin);
          setAdminFirstName(admin.firstName || "");
          setAdminLastName(admin.lastName || "");
          setAdminEmail(admin.mail || "");
          setAdminPhone(admin.phoneNumber || "");
        }
      }

      // Parameters
      if (paramsRes.data?.status === 200 && paramsRes.data.data) {
        const p = paramsRes.data.data;
        setParams({
          rowStatus: p.rowStatus || "",
          loadingRoundingSum: p.loadingRoundingSum || DEFAULT_PARAMS.loadingRoundingSum,
          minimumSumForReduction: p.minimumSumForReduction || DEFAULT_PARAMS.minimumSumForReduction,
          minimumPerDevice: p.minimumPerDevice || DEFAULT_PARAMS.minimumPerDevice,
          dayFraction: p.dayFraction || DEFAULT_PARAMS.dayFraction,
          specialDaysAmount: p.specialDaysAmount || DEFAULT_PARAMS.specialDaysAmount,
          weeklySum1: p.weeklySum1 ?? "",
          factor1: p.factor1 ?? "",
          weeklySum2: p.weeklySum2 ?? "",
          factor2: p.factor2 ?? "",
          weeklySum3: p.weeklySum3 ?? "",
          factor3: p.factor3 ?? "",
          manuallyCounted: p.manuallyCounted || DEFAULT_PARAMS.manuallyCounted,
          cleaningCardReader: p.cleaningCardReader || DEFAULT_PARAMS.cleaningCardReader,
          smsCost: p.smsCost ?? 0,
          whatsappCost: p.whatsappCost ?? 0,
          whatsappUtilityCost: p.whatsappUtilityCost ?? 0,
          whatsappMarketingCost: p.whatsappMarketingCost ?? 0,
          whatsappAuthCost: p.whatsappAuthCost ?? 0,
          smsSegmentSize: p.smsSegmentSize ?? 134,
        });
      }

      // Messages
      if (messagesRes.data?.status === 200) {
        setMessages(messagesRes.data.data || []);
      }
    } catch (err) {
      toast.error("Error loading company data");
    } finally {
      setIsLoading(false);
    }
  };

  // ===== Tab 1: Save Company =====
  const saveCompany = async () => {
    if (!companyName.trim()) {
      toast.error(t("REQUIRED") || "Required");
      return;
    }
    setIsLoading(true);
    try {
      if (isEditMode) {
        const res = await CompanyService.updateCompany({ id: companyId, companyName });
        if (res.data?.status === 200) {
          toast.success(res.data.message);
        } else {
          toast.error(res.data?.message);
        }
      } else {
        const res = await CompanyService.createCompany({ companyName, isLogin: true });
        if (res.data?.status === 200) {
          const newId = res.data.data?.id;
          setCompanyId(newId);
          toast.success(res.data.message);
          navigate(`/sysadmin/company/${newId}`, { replace: true });
        } else {
          toast.error(res.data?.message);
        }
      }
    } catch (err) {
      toast.error("Error saving company");
    } finally {
      setIsLoading(false);
    }
  };

  // ===== Tab 3: Save Parameters =====
  const saveParameters = async () => {
    if (!companyId) {
      toast.error(t("SYSADMIN.SAVE_COMPANY_FIRST") || "יש לשמור חברה קודם");
      return;
    }
    setIsLoading(true);
    try {
      const res = await SysAdminService.saveCompanyParameters({
        companyId,
        ...params,
        weeklySum1: params.weeklySum1 === "" ? null : parseFloat(params.weeklySum1),
        factor1: params.factor1 === "" ? null : parseFloat(params.factor1),
        weeklySum2: params.weeklySum2 === "" ? null : parseFloat(params.weeklySum2),
        factor2: params.factor2 === "" ? null : parseFloat(params.factor2),
        weeklySum3: params.weeklySum3 === "" ? null : parseFloat(params.weeklySum3),
        factor3: params.factor3 === "" ? null : parseFloat(params.factor3),
      });
      if (res.data?.status === 200) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data?.message);
      }
    } catch (err) {
      toast.error("Error saving parameters");
    } finally {
      setIsLoading(false);
    }
  };

  // ===== Tab 4: Save Messages =====
  const saveMessages = async () => {
    if (!companyId) {
      toast.error(t("SYSADMIN.SAVE_COMPANY_FIRST") || "יש לשמור חברה קודם");
      return;
    }
    setIsLoading(true);
    try {
      const res = await SysAdminService.saveMessagesToCompany({
        companyId,
        mappings: messages.map((m) => ({
          messageCode: m.messageCode,
          active: m.active,
        })),
      });
      if (res.data?.status === 200) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data?.message);
      }
    } catch (err) {
      toast.error("Error saving messages");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMessage = (messageCode) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.messageCode === messageCode ? { ...m, active: !m.active } : m
      )
    );
  };

  const handleParamChange = (field, value) => {
    setParams((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <React.Fragment>
      <div className="page-title-box d-flex align-items-center justify-content-between">
        <h4 className="mb-0">
          {isEditMode
            ? `${t("SYSADMIN.EDIT_COMPANY") || "עריכת חברה"} — ${companyName || `#${companyId}`}`
            : t("SYSADMIN.CREATE_COMPANY") || "הקמת חברה חדשה"}
        </h4>
      </div>

      <Card className="card-box">
        <CardBody>
          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "1" })}
                onClick={() => setActiveTab("1")}
                style={{ cursor: "pointer" }}
              >
                <i className="ri-building-line me-1"></i>
                {t("SYSADMIN.TAB_COMPANY") || "פרטי חברה"}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "2" })}
                onClick={() => setActiveTab("2")}
                style={{ cursor: "pointer" }}
                disabled={!isEditMode}
              >
                <i className="ri-user-star-line me-1"></i>
                {t("SYSADMIN.TAB_ADMIN") || "מנהל חברה"}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "3" })}
                onClick={() => setActiveTab("3")}
                style={{ cursor: "pointer" }}
                disabled={!isEditMode}
              >
                <i className="ri-calculator-line me-1"></i>
                {t("SYSADMIN.TAB_PARAMS") || "פרמטרי חישוב"}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "4" })}
                onClick={() => setActiveTab("4")}
                style={{ cursor: "pointer" }}
                disabled={!isEditMode}
              >
                <i className="ri-mail-settings-line me-1"></i>
                {t("SYSADMIN.TAB_MESSAGES") || "שיוך הודעות"}
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            {/* Tab 1: Company Details */}
            <TabPane tabId="1">
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>{t("COMPANY.NAME_LABEL") || "שם חברה"} *</Label>
                    <Input
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder={t("COMPANY.NAME_PLACEHOLDER") || "הזן שם חברה"}
                    />
                  </div>
                </Col>
              </Row>
              <Button color="primary" onClick={saveCompany} disabled={isLoading}>
                {isLoading ? <Spinner size="sm" /> : (isEditMode ? t("UPDATE_TITLE") || "עדכון" : t("SYSADMIN.CREATE") || "צור חברה")}
              </Button>
            </TabPane>

            {/* Tab 2: Company Admin */}
            <TabPane tabId="2">
              {existingAdmin ? (
                <>
                  <h5 className="mb-3">{t("SYSADMIN.ADMIN_DETAILS") || "פרטי מנהל חברה"}</h5>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>{t("FIRST_NAME_LABEL") || "שם פרטי"}</Label>
                        <Input value={adminFirstName} disabled />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>{t("LAST_NAME_LABEL") || "שם משפחה"}</Label>
                        <Input value={adminLastName} disabled />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>{t("EMAIL_LABEL") || "אימייל"}</Label>
                        <Input value={adminEmail} disabled />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>{t("PHONE_NO_LABEL") || "מספר טלפון"}</Label>
                        <Input value={adminPhone} disabled />
                        <small className="text-muted">{t("SYSADMIN.PHONE_IS_COMPANY_PHONE") || "טלפון זה ישמש כטלפון החברה"}</small>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>{t("SYSADMIN.USERNAME") || "שם משתמש"}</Label>
                        <Input value={existingAdmin.userName || ""} disabled />
                      </div>
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  <h5 className="mb-3">{t("SYSADMIN.CREATE_ADMIN") || "יצירת מנהל חברה"}</h5>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>{t("FIRST_NAME_LABEL") || "שם פרטי"} *</Label>
                        <Input
                          value={adminFirstName}
                          onChange={(e) => setAdminFirstName(e.target.value)}
                          placeholder={t("FIRST_NAME_PLACEHOLDER") || "הכנס שם פרטי"}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>{t("LAST_NAME_LABEL") || "שם משפחה"} *</Label>
                        <Input
                          value={adminLastName}
                          onChange={(e) => setAdminLastName(e.target.value)}
                          placeholder={t("LAST_NAME_PLACEHOLDER") || "הכנס שם משפחה"}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>{t("EMAIL_LABEL") || "אימייל"} *</Label>
                        <Input
                          type="email"
                          value={adminEmail}
                          onChange={(e) => setAdminEmail(e.target.value)}
                          placeholder={t("EMAIL_PLACEHOLDER") || "הכנס אימייל"}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>{t("PHONE_NO_LABEL") || "מספר טלפון"} *</Label>
                        <Input
                          value={adminPhone}
                          onChange={(e) => setAdminPhone(e.target.value)}
                          placeholder={t("PHONE_NO_PLACEHOLDER") || "הכנס מספר טלפון"}
                        />
                        <small className="text-muted">{t("SYSADMIN.PHONE_IS_COMPANY_PHONE") || "טלפון זה ישמש כטלפון החברה"}</small>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>{t("PASSWORD_LABEL") || "סיסמה"} *</Label>
                        <Input
                          type="password"
                          value={adminPassword}
                          onChange={(e) => setAdminPassword(e.target.value)}
                          placeholder={t("PASSWORD_PLACEHOLDER") || "הכנס את הסיסמה"}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Button color="primary" disabled={isLoading}>
                    {isLoading ? <Spinner size="sm" /> : t("SYSADMIN.CREATE_ADMIN_BTN") || "צור מנהל חברה"}
                  </Button>
                </>
              )}
            </TabPane>

            {/* Tab 3: Company Parameters */}
            <TabPane tabId="3">
              <h5 className="mb-3">{t("SYSADMIN.PARAMS_TITLE") || "פרמטרי חישוב"}</h5>
              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>{t("SYSADMIN.LINE_STATUS") || "סטטוס קו"}</Label>
                    <Input value={params.rowStatus} onChange={(e) => handleParamChange("rowStatus", e.target.value)} />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>{t("SYSADMIN.LOADING_ROUNDING") || "עיגול סכום טעינה"}</Label>
                    <Input type="number" value={params.loadingRoundingSum} onChange={(e) => handleParamChange("loadingRoundingSum", parseFloat(e.target.value) || 0)} />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>{t("SYSADMIN.MIN_REDUCTION") || "מינימום להפחתה"}</Label>
                    <Input type="number" value={params.minimumSumForReduction} onChange={(e) => handleParamChange("minimumSumForReduction", parseFloat(e.target.value) || 0)} />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>{t("SYSADMIN.MIN_PER_DEVICE") || "מינימום למכשיר"}</Label>
                    <Input type="number" value={params.minimumPerDevice} onChange={(e) => handleParamChange("minimumPerDevice", parseFloat(e.target.value) || 0)} />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>{t("SYSADMIN.DAY_FRACTION") || "חלק היום"}</Label>
                    <Input type="number" step="0.01" value={params.dayFraction} onChange={(e) => handleParamChange("dayFraction", parseFloat(e.target.value) || 0)} />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>{t("SYSADMIN.SPECIAL_DAYS_AMOUNT") || "מקדם ימים מיוחדים"}</Label>
                    <Input type="number" step="0.01" value={params.specialDaysAmount} onChange={(e) => handleParamChange("specialDaysAmount", parseFloat(e.target.value) || 0)} />
                  </div>
                </Col>
              </Row>

              <h6 className="mt-3 mb-2">{t("SYSADMIN.MAINTENANCE_PARAMS") || "פרמטרי תחזוקה"}</h6>
              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>{t("SYSADMIN.MANUALLY_COUNTED") || "ספירה ידנית (ימים)"}</Label>
                    <Input type="number" value={params.manuallyCounted} onChange={(e) => handleParamChange("manuallyCounted", parseInt(e.target.value) || 0)} />
                    <small className="text-muted">{t("SYSADMIN.MANUALLY_COUNTED_DESC") || "מספר ימים בין ספירות ידניות של מסופים"}</small>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <Label>{t("SYSADMIN.CLEANING_CARD_READER") || "ניקוי קורא כרטיסים (ימים)"}</Label>
                    <Input type="number" value={params.cleaningCardReader} onChange={(e) => handleParamChange("cleaningCardReader", parseInt(e.target.value) || 0)} />
                    <small className="text-muted">{t("SYSADMIN.CLEANING_CARD_READER_DESC") || "מספר ימים בין ניקויי קורא כרטיסים"}</small>
                  </div>
                </Col>
              </Row>

              <h6 className="mt-3 mb-2">{t("SYSADMIN.WEEKLY_FACTORS") || "מקדמים שבועיים"}</h6>
              <small className="text-muted d-block mb-2">{t("SYSADMIN.WEEKLY_FACTORS_DESC") || "סכום שבועי מינימלי והמקדם שיחול כאשר הסכום עובר את הסף"}</small>
              <Row>
                <Col md={2}><div className="mb-3"><Label>{t("SYSADMIN.WEEKLY_SUM") || "סכום שבועי"} 1</Label><Input type="number" value={params.weeklySum1} onChange={(e) => handleParamChange("weeklySum1", e.target.value)} /></div></Col>
                <Col md={2}><div className="mb-3"><Label>{t("SYSADMIN.FACTOR") || "מקדם"} 1</Label><Input type="number" step="0.01" value={params.factor1} onChange={(e) => handleParamChange("factor1", e.target.value)} /></div></Col>
                <Col md={2}><div className="mb-3"><Label>{t("SYSADMIN.WEEKLY_SUM") || "סכום שבועי"} 2</Label><Input type="number" value={params.weeklySum2} onChange={(e) => handleParamChange("weeklySum2", e.target.value)} /></div></Col>
                <Col md={2}><div className="mb-3"><Label>{t("SYSADMIN.FACTOR") || "מקדם"} 2</Label><Input type="number" step="0.01" value={params.factor2} onChange={(e) => handleParamChange("factor2", e.target.value)} /></div></Col>
                <Col md={2}><div className="mb-3"><Label>{t("SYSADMIN.WEEKLY_SUM") || "סכום שבועי"} 3</Label><Input type="number" value={params.weeklySum3} onChange={(e) => handleParamChange("weeklySum3", e.target.value)} /></div></Col>
                <Col md={2}><div className="mb-3"><Label>{t("SYSADMIN.FACTOR") || "מקדם"} 3</Label><Input type="number" step="0.01" value={params.factor3} onChange={(e) => handleParamChange("factor3", e.target.value)} /></div></Col>
              </Row>

              <h6 className="mt-3 mb-2">עלויות הודעות</h6>
              <Row>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>עלות הודעת SMS (₪)</Label>
                    <Input type="number" step="0.0001" value={params.smsCost} onChange={(e) => handleParamChange("smsCost", parseFloat(e.target.value) || 0)} />
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>מספר תווים למקטע SMS</Label>
                    <Input type="number" value={params.smsSegmentSize || 134} onChange={(e) => handleParamChange("smsSegmentSize", parseInt(e.target.value) || 134)} />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>עלות WhatsApp - Utility (₪)</Label>
                    <Input type="number" step="0.0001" value={params.whatsappUtilityCost} onChange={(e) => handleParamChange("whatsappUtilityCost", parseFloat(e.target.value) || 0)} />
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>עלות WhatsApp - Marketing (₪)</Label>
                    <Input type="number" step="0.0001" value={params.whatsappMarketingCost} onChange={(e) => handleParamChange("whatsappMarketingCost", parseFloat(e.target.value) || 0)} />
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>עלות WhatsApp - Auth (₪)</Label>
                    <Input type="number" step="0.0001" value={params.whatsappAuthCost} onChange={(e) => handleParamChange("whatsappAuthCost", parseFloat(e.target.value) || 0)} />
                  </div>
                </Col>
              </Row>

              <Button color="primary" onClick={saveParameters} disabled={isLoading}>
                {isLoading ? <Spinner size="sm" /> : t("SUBMIT") || "שמור"}
              </Button>
            </TabPane>

            {/* Tab 4: Messages to Company */}
            <TabPane tabId="4">
              <h5 className="mb-3">{t("SYSADMIN.MESSAGES_TITLE") || "שיוך הודעות לחברה"}</h5>
              <Table striped bordered responsive size="sm">
                <thead>
                  <tr>
                    <th style={{ width: "60px" }}>#</th>
                    <th>{t("SYSADMIN.MESSAGE_NAME") || "סוג הודעה"}</th>
                    <th style={{ width: "100px" }} className="text-center">
                      {t("SYSADMIN.ACTIVE") || "פעיל"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((m) => (
                    <tr key={m.messageCode}>
                      <td>{m.messageCode}</td>
                      <td>{m.messageName || m.messageCodeDescription}</td>
                      <td className="text-center">
                        <Input
                          type="checkbox"
                          checked={m.active}
                          onChange={() => toggleMessage(m.messageCode)}
                          style={{ cursor: "pointer" }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Button color="primary" onClick={saveMessages} disabled={isLoading}>
                {isLoading ? <Spinner size="sm" /> : t("SUBMIT") || "שמור"}
              </Button>
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </React.Fragment>
  );
}

export default CompanySetup;
